import React, {
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useContext,
  createContext,
  type PropsWithChildren,
} from "react";
import { createStore, useStore, type StoreApi } from "zustand";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { StillPageContext } from "../design/stillPage";
import type {
  ViewConfig,
  AggregationKind,
  EntityRow,
  EntityCellValue,
  FrontendFilter,
  FilterOp,
  RmsSchema,
} from "../Dashboard/types";

import {
  insertClauseInNewGroup,
  insertClauseIntoGroup,
  stripDisplayFields,
} from "./Filter/filterJoins";
import { isFilterEmpty as isClauseEmpty } from "./Filter/filterPredicates";
import {
  useSearchEntities,
  type NameMatch,
  type SearchParams,
  searchQueryKeyFor,
  SEARCH_STALE_TIME,
} from "../Data/search";
import { useRecordDataSource } from "../Dashboard/recordDataSource";
import { GROUP_ROW_LIMIT, withMoreRowsFor } from "./groupRows";
import type { TableConfig } from "../Data/views";
import { useDebouncedCallback } from "@/hooks/useDebouncedCallback";

// =============================================================================
// Defaults / pure helpers
// =============================================================================

function getAggregationDefaultSort(panelKind: AggregationKind): string {
  switch (panelKind) {
    case "document":
      return "Uploaded At";
    case "shadowbook":
      return "Uploaded At";
  }
}

// Per-view-type default column set used when neither the user nor a saved
// view has selected columns. Names are filtered against the live schema so
// stale entries (e.g. a builtin renamed by a schema change) don't render
// as ghost columns.
//
// Activity and aggregation/document views have hard-coded picks because their
// schemas front-load fields the table renders elsewhere (the activity grid
// has its own name column off, and the document aggregation renders filename
// as the Name column). The legacy "first 4 facts" fallback ended up empty
// (activity, after the polymorphic Target / Source land in the slice) or
// collapsed to just the Name column (documents, where slice(0,4) is
// dominated by filename which `computeOrderedFacts` strips). Picking
// sensible columns explicitly avoids both failure modes (#9618).
// An entity's own Interactions tab — the one collection whose default
// column set is tuned to its scope (round 9). One definition, consumed by
// the default-column builder, the seeding effect, and the data-driven
// prune, so the three cannot drift.
export function isEntityScopedInteractionView(viewConfig: ViewConfig): boolean {
  return (
    viewConfig.type === "search" &&
    viewConfig.entityKind === "interaction" &&
    Boolean(viewConfig.scope?.publicId)
  );
}

// The layout a table SEEDS when nothing chose one — the answer applied on
// first mount (by `useConfiguredColumnLayout` for transactions and holdings,
// which also re-applies it when the configured layout changes, and by
// `useSearchParamsSync` for every other view), and therefore the answer
// anything asking "has the layout drifted from the default?" must use
// (gaps-v12 item 4 review B1: the tab-view chips once asked
// `getDefaultVisibleColumns` directly, which for an aggregation view names
// fact ids no schema fact carries, so a fresh Documents tab was "dirty"
// forever and Discard collapsed it to one column; #12500: a separate
// "initial columns" function seeded transactions with the configured layout
// while the change bar compared against the first 7, so a transactions view
// with more than seven columns opened "dirty"). A host that already has the
// schema when it builds its store starts the store on these too, so the
// first search names its columns instead of being followed by a second one
// once the grid seeds them (Slack C073TAECDDG/1790792255.604109).
// `document_review`, a process view, the entity-scoped interactions view,
// transactions and holdings (each the organization's configured layout,
// #11118) carry configured defaults; every other view keeps the legacy
// first-7 pick.
export function seededVisibleColumns(
  viewConfig: ViewConfig,
  schema: RmsSchema
): string[] {
  // A record page's layout names where its Documents Table starts (#13370,
  // round 32b); the names the schema no longer has drop out.
  if (viewConfig.type === "aggregation" && viewConfig.defaultColumns) {
    const known = new Set(schema.facts.map((fact) => fact.name));
    const named = viewConfig.defaultColumns.filter((name) => known.has(name));
    if (named.length > 0) return named;
  }
  const hasConfiguredDefault =
    viewConfig.type === "transactions" ||
    viewConfig.type === "holdings" ||
    viewConfig.type === "document_review" ||
    viewConfig.type === "process" ||
    isEntityScopedInteractionView(viewConfig);
  return hasConfiguredDefault
    ? getDefaultVisibleColumns(viewConfig, schema)
    : schema.facts.slice(0, 7).map((fact) => fact.name);
}

/** The holdings schema's built-in snapshot date column. */
const HOLDINGS_SNAPSHOT_DATE_COLUMN = "Snapshot date";

export function getDefaultVisibleColumns(
  viewConfig: ViewConfig,
  schema: RmsSchema
): string[] {
  const factNames = new Set(schema.facts.map((f) => f.name));
  const filterToSchema = (names: string[]): string[] =>
    names.filter((n) => factNames.has(n));

  if (viewConfig.type === "process") {
    return filterToSchema([...viewConfig.view.defaultColumns()]);
  }
  if (viewConfig.type === "activity") {
    return filterToSchema(["Time", "Actor", "Action", "Target", "Target Kind"]);
  }
  if (viewConfig.type === "document_review") {
    return filterToSchema([
      // The name this document's research file gets in Bipsync (#11311). It
      // leads so it sits beside the Name column: a reviewer compares the
      // document's own name against the name it will carry in Bipsync, and
      // edits the latter in place.
      "Bipsync Title",
      "Note type",
      "Subtype",
      "Funds",
      "Managers",
      "Companies",
      "Effective date",
      "Uploaded at",
      "Status",
      "New entities?",
    ]);
  }
  // The transactions schema is the whole Caissa catalog, so the add-column
  // control has something to offer. The organization's configured layout is
  // carried on it as `position`: numbered facts are the layout, in its order,
  // and an unplaced fact (null) is available but hidden until someone adds it.
  //
  // Holdings takes the same path (#10835): its schema is the two synthetic
  // columns plus the org's configured holdings_columns, every one positioned.
  // The generic 4-column fallback below would otherwise hide every configured
  // column past the fourth, so the view has to declare its layout by position
  // just like transactions.
  // A dataset view (#12178) is the same shape again: every column is declared
  // with a position, and the declaration is the layout.
  if (
    viewConfig.type === "transactions" ||
    viewConfig.type === "holdings" ||
    viewConfig.type === "dataset"
  ) {
    const layout = schema.facts
      .filter((f) => f.position !== null && f.position !== undefined)
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
      .map((f) => f.name);
    // A record's Holdings tab names its snapshot's date in its snapshot
    // control (#14991), so the column starts hidden there; Columns still
    // offers it. The global Holdings view keeps it.
    return viewConfig.type === "holdings" && viewConfig.scope
      ? layout.filter((name) => name !== HOLDINGS_SNAPSHOT_DATE_COLUMN)
      : layout;
  }
  if (
    viewConfig.type === "aggregation" &&
    viewConfig.panelKind === "document"
  ) {
    return filterToSchema([
      "uploaded_at",
      "document_type",
      "document_group",
      "document_date",
    ]);
  }
  // An entity's own Interactions tab (round 8): two default columns carry
  // zero information there. Source only means something in a mixed
  // collection; Participants, while the participant-only filter is on, is
  // the record you are standing on repeated in every row. Both stay
  // available through Add column.
  if (isEntityScopedInteractionView(viewConfig)) {
    const scopedOut = new Set(
      viewConfig.type === "search" && viewConfig.scope?.participantOnly
        ? ["Source", "Participants"]
        : ["Source"]
    );
    return schema.facts
      .map((f) => f.name)
      .filter((n) => !scopedOut.has(n))
      .slice(0, 7);
  }
  return schema.facts.slice(0, 4).map((f) => f.name);
}

// Whether a saved column layout names something the schema no longer has.
//
// A view stores its layout as column *names*, so a schema change can leave it
// pointing at a column that is gone — and `computeOrderedFacts` drops an
// unresolvable name silently, so the grid renders fewer columns than the
// organization configured with nothing on screen to say why. One production
// organization saw two columns out of eight this way, its saved layout still
// naming three built-ins that #10997 removed (#11129).
//
// "Stale" is deliberately *names a column that does not exist* rather than
// *names fewer columns than the schema offers*. The latter is indistinguishable
// from a user who hid columns on purpose, and that selection has to survive.
export function isStaleColumnLayout(
  visibleColumns: string[] | undefined,
  schema: RmsSchema
): boolean {
  // Nothing saved, or nothing to check against. A schema that has not loaded
  // yet cannot answer the question, and answering "stale" would discard a good
  // layout on the first render.
  if (!visibleColumns?.length || schema.facts.length === 0) return false;
  const factNames = new Set(schema.facts.map((f) => f.name));
  return visibleColumns.some((name) => !factNames.has(name));
}

export const DEFAULT_PAGE_SIZE = 50;

// When grouping is active, pagination counts groups (not rows). Backend
// already emits rows whose `group_rank <= pageSize + 1`; this cuts
// rawData at the first row whose rank exceeds pageSize, so the UI
// always receives whole groups. For ungrouped queries, the classic
// row-count cap + trailing-row hasMore sentinel applies.
function sliceForPage(
  rawData: EntityRow[],
  searchState: SearchState
): { data: EntityRow[]; hasMore: boolean } {
  const grouped = searchState.groupBy.length > 0;
  if (grouped) {
    const cutoff = rawData.findIndex(
      (r) => (r.group_rank ?? 0) > searchState.pageSize
    );
    if (cutoff === -1) {
      return { data: rawData, hasMore: false };
    }
    return { data: rawData.slice(0, cutoff), hasMore: true };
  }
  const hasMore = rawData.length > searchState.pageSize;
  return {
    data: hasMore ? rawData.slice(0, searchState.pageSize) : rawData,
    hasMore,
  };
}

// =============================================================================
// Types
// =============================================================================

export interface SortEntry {
  column: string;
  order: "asc" | "desc";
}

// A new field is either something a request carries or layout only. The
// first kind is compared in `searchStatesEqual`; the second is left out there
// and read live in `useDispatchedSearchState`. Put it in exactly one.
export interface SearchState {
  viewConfig: ViewConfig;
  filters: FrontendFilter[];
  pageSize: number;
  keysetId: string | null;
  // Extra items to skip *beyond* `keysetId` — the span a forward jump
  // passes over (#10593). 0 on every ordinary next step, so adjacent
  // paging stays pure keyset. Meaningless without a cursor: a cold jump's
  // skip is implied by `page` and derived in createQueryParams, where the
  // page size in effect is known.
  pageOffset: number;
  // The page the user is on (1-based). Authoritative: every navigation
  // sets it explicitly, so the paginator never has to infer it from the
  // cursor. It's also what the URL carries, which is what makes a
  // bookmarked page reconstructible (#10593).
  page: number;
  searchText: string;
  visibleColumns: string[] | undefined; // ordered list of visible column names (encodes both visibility and display order)
  columnWidths: Record<string, number> | undefined; // column name -> width in pixels
  sorts: SortEntry[]; // multi-column sort specifications
  groupBy: string[]; // ordered list of column names to group rows by; empty = no grouping
  // Board projection layout (#11426 gaps-v6 item 1). Undefined/null =
  // the page default (boardLayout.ts). Rides the same save/apply/drift
  // pipeline as columns and sorts, so a view keeps its board.
  boardAxis?: string | null;
  boardCard?: { subtitle: string | null; fields: string[] } | null;
  // Transient (UI-only, not persisted/serialized): true once the user has
  // explicitly chosen a sort. During a search this gates whether the column
  // sort is sent — and thus whether the relevance cutoff applies (#8908) —
  // so a fresh search stays on the relevance-ordered, no-cutoff path.
  userSorted?: boolean;
  // Transient: the type-discriminator column the data-driven default prune
  // removed, with the constant RAW value it saw (#11426, design round 10).
  // The footer sentence states it ("all Meetings") — losing the attribute
  // costs nothing, but losing what the rows ARE must not happen silently.
  // Recorded at prune time because later responses no longer carry the
  // pruned column's cells; the column returns on the next seeded default
  // the moment the page's values stop being constant.
  prunedDiscriminator?: { column: string; value: string } | null;
  // Transient: rows allowed per group beyond GROUP_ROW_LIMIT, keyed by the
  // page-relative `group_rank` ("Show more"). Part of the page's request, so
  // a refetch returns the same rows; any change to what the page is (a
  // filter, a sort, another page) clears it, since ranks are page-relative.
  // Never saved in a view or projected into the URL.
  groupRowLimits?: Record<number, number>;
}

// How long a typed change waits for the next keystroke before its search is
// sent (`SetSearchStateOptions.debounce`). Longer than the gap between an
// ordinary typist's keystrokes: at 150 ms only a fast typist's letters were
// coalesced, and everyone else sent a search per letter (#12511).
export const TYPED_SEARCH_DEBOUNCE_MS = 300;

// The page window: moving it is paging, not a new question.
const PAGE_WINDOW_KEYS: ReadonlySet<keyof SearchState> = new Set([
  "keysetId",
  "pageOffset",
  "page",
]);

// How the rows are laid out, never which rows: a change here keeps the data
// on screen. Board layout counts — regrouping lanes or recomposing cards must
// not refetch or snap the page (#11426 gaps-v6 item 1).
const LAYOUT_KEYS: ReadonlySet<keyof SearchState> = new Set([
  "columnWidths",
  "visibleColumns",
  "boardAxis",
  "boardCard",
]);

// Everything that never changes the question: the layout, and the pruned
// discriminator, pure footer copy that rides along with the prune's
// visibleColumns write. These neither snap the page nor drop the page's
// group allowances.
const PRESENTATION_KEYS: ReadonlySet<keyof SearchState> = new Set([
  ...LAYOUT_KEYS,
  "prunedDiscriminator",
]);

export interface SetSearchStateOptions {
  // When true, the next fetch waits out TYPED_SEARCH_DEBOUNCE_MS — used for text-input
  // streams where each keystroke shouldn't fire a request. Defaults to
  // false: clicks (sort, filter chip, group-by, pagination) fetch
  // immediately so the UI feels responsive.
  debounce?: boolean;
  // When true, a non-pagination change keeps the page window the caller
  // names instead of resetting to page 1 — how a page-size change stays on
  // the row being read (#11426). Explicit, so a caller that happens to
  // spread a full state (retry paths) cannot trip it by key presence.
  keepPage?: boolean;
}

interface TableSearchStore {
  // The view at rest — what the active view would render if the user had
  // taken no action since opening it. Derived from `RmsView.config` plus
  // framework defaults (sort, page size). Mutated only by `setServerState`
  // when the view's saved config changes (Save flow). Diffed against
  // `currentSearch` to decide isDirty and project the URL.
  serverState: SearchState;
  currentSearch: SearchState;
  previousSearch: SearchState | null;
  lastGoodSearch: SearchState | null;
  intermediarySearch: SearchState | null;
  // The last search handed to the server, set on every dispatch and never
  // cleared (#12511). `intermediarySearch` and `previousSearch` are both
  // emptied when a search fails or the table is refreshed, so neither can say
  // what was last asked for while the next typed change waits.
  lastDispatchedSearch: SearchState | null;
  data: EntityRow[] | undefined;
  /** The holdings search's column totals for `data`'s search (#14991). */
  columnTotals: Record<string, number> | null;
  error: string | null;
  hasMore: boolean;
  pendingDebounce: boolean;
  // Armed by the chip strip's group controls (#10851): where the next
  // clause the filters panel inserts should land — "new" starts a new
  // top-level OR group, a number targets that existing group, null means
  // the default insert. Transient UI state — never serialized into views
  // or URLs.
  pendingGroupTarget: number | "new" | null;
  // Whether the filters panel is currently open (synced by
  // SearchTabContent). The strip's "+" menu hides "Add filter" while
  // the panel is open — its only effect is opening the panel, so with
  // it open the item appears to do nothing.
  filtersPanelOpen: boolean;
  // Rows this user has edited since the last user-initiated result set
  // (#11363). Retention is keyed on this rather than on "rows that used to
  // match" so a row that vanished for any other reason — deleted by someone
  // else, removed by a background job — is still dropped rather than
  // lingering as a ghost. Transient: never serialized into views or URLs.
  editedRowIds: Set<string>;

  setSearchState: (
    state: Partial<SearchState>,
    options?: SetSearchStateOptions
  ) => void;
  setIntermediarySearch: (searchState: SearchState) => void;
  setDataAndPreviousSearch: (
    data: EntityRow[] | undefined,
    searchState: SearchState,
    hasMore: boolean,
    columnTotals?: Record<string, number> | null
  ) => void;
  /**
   * Apply a background refetch — one triggered by a write invalidating the
   * rms queries, not by the user asking a new question (#11363).
   *
   * Same bookkeeping as `setDataAndPreviousSearch`, except rows this user
   * edited that have dropped out of the result set are merged back in at
   * their prior index and marked `_retained`. Without this, filling in a
   * cell under a "No Value" filter evicts the row the user is standing on
   * a second later, which defeats fill-down.
   */
  applyBackgroundRefetch: (
    data: EntityRow[] | undefined,
    searchState: SearchState,
    hasMore: boolean,
    columnTotals?: Record<string, number> | null
  ) => void;
  /** Drop retained rows and forget the edits holding them (#11363). */
  clearRetainedRows: () => void;
  setError: (error: string | null) => void;
  setPendingGroupTarget: (target: number | "new" | null) => void;
  setFiltersPanelOpen: (open: boolean) => void;
  /**
   * The chip strip asks for the filters panel, at a field when it names one (a chip's click,
   * the add menu, a new OR group). A store made with `ownsFilterPanel` keeps the request in
   * `openFilterRequest` for its host; any other announces it on the window as
   * `rms-open-filter`, where the page that hosts the strip listens.
   */
  requestOpenFilter: (fieldName: string | null) => void;
  /** The latest request for the filters panel, in a store made with `ownsFilterPanel`. */
  openFilterRequest: OpenFilterRequest | null;
  /** Update the server baseline after the view's saved config changes (Save flow). */
  setServerState: (
    viewConfig: ViewConfig,
    serverDefaults?: Partial<Omit<SearchState, "viewConfig">>
  ) => void;
  updateEntityFact: (
    entityId: string,
    factId: string,
    newValue: EntityCellValue
  ) => void;
  updateEntityName: (entityId: string, newName: string) => void;
  /** Drop visible data + force re-fetch. Keeps serverState. */
  clearData: () => void;
  /** Force a data refresh by marking state as stale so the fetch cycle re-runs */
  forceRefresh: () => void;
  /**
   * Navigate to `target` (1-based).
   *
   * `cursor` is the current page's last row's keyset_id, when the caller
   * has one. Jumping forward rides it and offsets only the span being
   * skipped, so the cost tracks the distance jumped rather than how deep
   * the user is. Jumping backward can't (a cursor only points forward),
   * so it re-jumps from the top — still cheaper than where they were.
   */
  goToPage: (target: number, cursor: string | null) => void;
  resetToFirstPage: () => void;
}

// =============================================================================
// Filter helpers
// =============================================================================

// Which clauses go out on the wire. Delegates to the shared predicate in
// Filter/filterPredicates rather than re-deciding here: this file used to carry its
// own copy, and the two drifted (#11243). The copy dropped any clause without a
// `value`, which is every presence clause, so "Filename has no value" shipped a
// request with no filter at all while the chip and the count badge -- both
// reading the shared helper -- said it was applied. It also kept an `in` clause
// holding an empty list, which the shared helper drops.
const getValidFilters = (filters: FrontendFilter[]): FrontendFilter[] => {
  return filters.filter((filter) => !isClauseEmpty(filter));
};

/**
 * Merge a background refetch over the rows already on screen, holding back
 * rows this user edited that the incoming result set no longer contains
 * (#11363).
 *
 * The incoming set is authoritative for order and content — a row still
 * matching takes its fresh server copy.
 *
 * A retained row goes back **directly below the nearest earlier row that
 * survived**, rather than at the raw index it used to occupy. Those differ
 * whenever the server returns a row the table has not seen before: a new row
 * arriving at the head shifts every index by one, and re-inserting by index
 * would lift the retained row above a neighbour it was never above. Walking
 * `previous` in ascending order also means an earlier retained row is
 * already in `merged` by the time it is needed as the next one's anchor, so
 * a run of adjacent retained rows keeps its original order.
 *
 * In a **grouped** view the row keeps belonging to its own group — the user
 * edited some other column, so its group value has not changed — and that is
 * where it goes. What it cannot keep is its `group_rank`: the server computes
 * that as a DENSE_RANK over the filtered result set, so if the row's group
 * loses its last matching member, every later group's rank shifts down and
 * the stale rank lands on a *different* group. The grid builds group runs
 * from adjacent equal ranks, so that collision would drag a real,
 * still-matching row under the wrong heading. So the rank is re-derived:
 * taken from the row's own group when that group is still on the page, and
 * otherwise replaced with a negative rank, which cannot collide (a
 * DENSE_RANK is always >= 1) and therefore heads its own run labelled from
 * the row's own group value.
 */
const mergeRetainedRows = (
  previous: EntityRow[],
  incoming: EntityRow[],
  editedRowIds: Set<string>,
  grouped: boolean
): EntityRow[] => {
  const incomingIds = new Set(incoming.map((row) => row.id));
  const merged: EntityRow[] = [...incoming];
  // Negative ranks for retained rows whose group has no members left. Counts
  // down so two such rows from *different* vanished groups never share a run.
  let orphanRank = 0;

  // Directly below the nearest earlier row that survived — see above.
  const anchorFor = (index: number): number => {
    for (let i = index - 1; i >= 0; i--) {
      const at = merged.findIndex((m) => m.id === previous[i].id);
      if (at !== -1) return at + 1;
    }
    return 0;
  };

  previous.forEach((row, index) => {
    if (incomingIds.has(row.id)) return;
    if (!editedRowIds.has(row.id)) return;

    if (!grouped) {
      merged.splice(anchorFor(index), 0, { ...row, _retained: true });
      return;
    }

    // Rejoin its own group if that group is still on the page. Searching
    // `merged` rather than `incoming` means a second retained row from the
    // same vanished group joins the first instead of opening another run.
    const lastOfGroup =
      row.group_value == null
        ? -1
        : merged.findLastIndex((m) => m.group_value === row.group_value);

    if (lastOfGroup !== -1) {
      merged.splice(lastOfGroup + 1, 0, {
        ...row,
        group_rank: merged[lastOfGroup].group_rank,
        _retained: true,
      });
      return;
    }

    orphanRank -= 1;
    merged.splice(anchorFor(index), 0, {
      ...row,
      group_rank: orphanRank,
      _retained: true,
    });
  });
  return merged;
};

// Migrate old-format filters (from bookmarked URLs or saved views) to new format
export const migrateOldFilters = (raw: any[]): FrontendFilter[] => {
  return raw.flatMap((f: any) => {
    if (f.op) return [f as FrontendFilter];
    const results: FrontendFilter[] = [];
    if (f.equals != null)
      results.push({ name: f.name, op: "eq", value: f.equals });
    if (f.equals_many?.length)
      results.push({ name: f.name, op: "in", value: f.equals_many });
    if (f.between)
      results.push({ name: f.name, op: "between", value: f.between });
    if (f.prefix != null)
      results.push({ name: f.name, op: "prefix", value: f.prefix });
    if (f.prefix_many?.length)
      results.push({ name: f.name, op: "prefix_many", value: f.prefix_many });
    if (f.entity_is)
      results.push({ name: f.name, op: "ref_eq", value: f.entity_is });
    if (f.entity_is_many?.length)
      results.push({
        name: f.name,
        op: "ref_in",
        value: f.entity_is_many,
        selected_entities: f.selected_entities,
      });
    if (f.similar)
      results.push({ name: f.name, op: "similar", value: f.similar });
    if (results.length === 0)
      results.push({ name: f.name, op: "eq" as FilterOp });
    return results;
  });
};

// Migrate legacy interaction field names to schema fact names. The date
// column is "Date" since #12394 round 18 (an upcoming meeting has not
// occurred); a view saved before it still says "Occurred At".
const LEGACY_INTERACTION_NAMES: Record<string, string> = {
  occurred_at: "Date",
  "Occurred At": "Date",
  meeting_type: "Interaction Type",
  Type: "Interaction Type",
  primary_participants: "Participants",
  summary: "Summary",
};

const migrateInteractionName = (name: string): string =>
  LEGACY_INTERACTION_NAMES[name] ?? name;

const migrateDateName = (name: string): string =>
  name === "Occurred At" || name === "occurred_at" ? "Date" : name;

// Wire-format stripping lives in Filter/filterJoins.ts so this builder
// and the export builder share one field whitelist.

// =============================================================================
// State derivation
// =============================================================================

// Migrate legacy column / sort names so server baseline and current state
// share the same canonical names — otherwise the dirty check / URL diff
// would treat a legacy-named view as always-different.
function migrateForView(
  state: SearchState,
  viewConfig: ViewConfig
): SearchState {
  const isInteraction =
    viewConfig.type === "search" && viewConfig.entityKind === "interaction";
  const isActivity = viewConfig.type === "activity";
  let next = state;
  if (isActivity && next.sorts) {
    const ID_TO_NAME: Record<string, string> = {
      occurred_at: "Time",
      action: "Action",
      user_id: "Actor",
      llm_reasoning: "Reasoning",
    };
    next = {
      ...next,
      sorts: next.sorts.map((s) => ({
        ...s,
        column: ID_TO_NAME[s.column] ?? s.column,
      })),
    };
  }
  if (isInteraction) {
    const sorts = next.sorts?.map((s) => ({
      ...s,
      column: migrateInteractionName(s.column),
    }));
    const visibleColumns = next.visibleColumns?.map(migrateInteractionName);
    let columnWidths = next.columnWidths;
    if (columnWidths) {
      const migrated: Record<string, number> = {};
      for (const [k, v] of Object.entries(columnWidths)) {
        migrated[migrateInteractionName(k)] = v;
      }
      columnWidths = migrated;
    }
    // Saved filters, grouping and the board name the date column too: a
    // filter on "Occurred At" opens as a filter on Date (#12394 round 18).
    // Only the date column's former names, which the backend reads as Date
    // as well; the older aliases above were never sent there.
    const filters = next.filters?.map((f) => ({
      ...f,
      name: migrateDateName(f.name),
    }));
    const groupBy = next.groupBy?.map(migrateDateName);
    const boardAxis = next.boardAxis
      ? migrateDateName(next.boardAxis)
      : next.boardAxis;
    const boardCard = next.boardCard
      ? {
          subtitle: next.boardCard.subtitle
            ? migrateDateName(next.boardCard.subtitle)
            : next.boardCard.subtitle,
          fields: next.boardCard.fields.map(migrateDateName),
        }
      : next.boardCard;
    next = {
      ...next,
      sorts: sorts ?? next.sorts,
      visibleColumns,
      columnWidths,
      filters: filters ?? next.filters,
      groupBy: groupBy ?? next.groupBy,
      boardAxis,
      boardCard,
    };
  }
  // Stored configs accumulated the same column under several legacy aliases;
  // after the alias mapping above those collapse onto one canonical name, so
  // the list must dedupe or the Columns menu renders triplets (#11426 N2).
  // First occurrence keeps the position.
  if (next.visibleColumns) {
    next = { ...next, visibleColumns: [...new Set(next.visibleColumns)] };
  }
  return next;
}

/**
 * Build the server baseline for a view — what the view would render if the
 * user had taken no action since opening it. baseState (framework defaults)
 * overlaid with the view's saved config, then migrated for legacy names.
 */
/**
 * The sort a view of this shape opens with when its saved definition sets
 * none. ONE definition, used both to initialise the table state and to decide
 * whether the state is dirty — a second copy in the unsaved-changes compare
 * hardcoded name-asc, so every interactions / aggregation / activity view
 * whose saved config carried no sorts opened already showing Discard/Save
 * (#11426, keeper ruling C6).
 *
 * Must match the SchemaFact.name (not .id) so header-click sort — which
 * dispatches by fact.name — finds the existing entry and toggles instead of
 * appending a second column. Document review defaults to Status ascending so
 * "pending" rows (awaiting review) sort to the top, ahead of
 * "queued"/"synced".
 */
export function defaultSortsFor(
  viewConfig: ViewConfig
): { column: string; order: "asc" | "desc" }[] {
  // One entry per view type, stated flatly so a new type is a visible
  // decision here rather than a fallthrough. "Date" on transactions is the
  // builtin column every ledger row physically has — the generic "name"
  // default named a column the transactions search drops, so the footer
  // sentence claimed a sort the grid wasn't in (#11426).
  const isInteraction =
    viewConfig.type === "search" && viewConfig.entityKind === "interaction";
  if (isInteraction) return [{ column: "Date", order: "desc" }];
  switch (viewConfig.type) {
    case "activity":
      return [{ column: "Time", order: "desc" }];
    case "aggregation":
      return [
        {
          column: getAggregationDefaultSort(viewConfig.panelKind),
          order: "desc",
        },
      ];
    case "document_review":
      return [{ column: "Status", order: "asc" }];
    case "process":
      return viewConfig.view.defaultSort;
    case "transactions":
      return [{ column: "Date", order: "desc" }];
    case "dataset":
      // No default: a dataset's columns are its own, so there is no column
      // name to assume. The backend's order (load order) is stable.
      return [];
    case "tasks":
      // What is due first, first; the backend puts undated tasks last.
      return [{ column: "Due", order: "asc" }];
    case "search":
    case "holdings":
    case "performance":
      return [{ column: "name", order: "asc" }];
  }
  // Exhaustiveness backstop: this tsconfig has noImplicitReturns off, so
  // without the `never` check a new ViewConfig member would compile and
  // silently return undefined here.
  return ((exhausted: never): never => {
    throw new Error(
      `defaultSortsFor: unhandled view type ${JSON.stringify(exhausted)}`
    );
  })(viewConfig);
}

// Exported for the unsaved-changes compare: dirty is judged against the
// NORMALIZED saved definition — the exact state this pipeline would seed from
// the saved config — never against the raw config, whose legacy column names
// and sort shape made a freshly opened view read as edited (#11426, keeper
// ruling C6).
export function computeServerState(
  viewConfig: ViewConfig,
  serverDefaults?: Partial<Omit<SearchState, "viewConfig">>
): SearchState {
  const baseState: SearchState = {
    viewConfig,
    filters: [],
    pageSize: DEFAULT_PAGE_SIZE,
    keysetId: null,
    pageOffset: 0,
    page: 1,
    searchText: "",
    visibleColumns: undefined,
    columnWidths: undefined,
    sorts: defaultSortsFor(viewConfig),
    groupBy: [],
    boardAxis: null,
    boardCard: null,
  };
  return migrateForView({ ...baseState, ...serverDefaults }, viewConfig);
}

// Review-column trio on activity views — presence of any one in the
// user's visible-columns list implies all three. Keeps "Reviewed"
// from showing up without "Approved by" / "Approved at" when a saved
// view was created before those columns existed.
const ACTIVITY_REVIEW_COLUMNS: readonly string[] = [
  "Reviewed",
  "Approved by",
  "Approved at",
];

function expandReviewTrio(
  cols: string[],
  schema: RmsSchema | undefined
): string[] {
  if (!schema) return cols;
  const factNames = new Set(schema.facts.map((f) => f.name));
  const allPresentInSchema = ACTIVITY_REVIEW_COLUMNS.every((n) =>
    factNames.has(n)
  );
  if (!allPresentInSchema) return cols;
  const colsSet = new Set(cols);
  const hasAny = ACTIVITY_REVIEW_COLUMNS.some((n) => colsSet.has(n));
  if (!hasAny) return cols;
  const anchor = cols.findIndex((n) => ACTIVITY_REVIEW_COLUMNS.includes(n));
  const missing = ACTIVITY_REVIEW_COLUMNS.filter((n) => !colsSet.has(n));
  if (missing.length === 0) return cols;
  const next = [...cols];
  next.splice(anchor + 1, 0, ...missing);
  return next;
}

// =============================================================================
// Query/URL helpers
// =============================================================================

// How a search term is applied to a request (#11804). Both narrow to the
// records the term finds by name or by what they hold (#12773). The table
// never passes this, and the backend ranks name matches first. The board
// passes "contains" — a lane has no visible ranking to make one legible —
// and keeps the view's own sort, since the backend then applies no
// relevance ordering.
export interface QueryParamsOptions {
  nameMatch?: NameMatch;
}

export const createQueryParams = (
  searchState: SearchState,
  options: QueryParamsOptions = {}
): SearchParams => {
  const validFilters = getValidFilters(searchState.filters);
  // A bare search stays relevance-ordered (no sort sent → the backend ranks
  // the matches). Once the user explicitly sorts during a search, send the
  // sort so the backend orders the matches by the column (#8908).
  const hasSearch =
    !!searchState.searchText && searchState.searchText.trim() !== "";
  const nameMatch = hasSearch ? options.nameMatch : undefined;
  const includeSort =
    !hasSearch || !!searchState.userSorted || nameMatch !== undefined;
  const activeSorts =
    includeSort && searchState.sorts.length > 0 ? searchState.sorts : [];
  return {
    viewConfig: searchState.viewConfig,
    page_size: searchState.pageSize + 1, // Request one extra to detect more pages
    filter:
      validFilters.length > 0
        ? JSON.stringify(stripDisplayFields(validFilters))
        : undefined,
    columns: searchState.visibleColumns || undefined,
    name: searchState.searchText || undefined,
    name_match: nameMatch,
    keyset_id: searchState.keysetId || undefined,
    // Two ways to reach a page, so two ways to measure the skip (#10593):
    // riding a cursor, the stored offset is the span jumped past it; with
    // no cursor it's a cold jump, and the skip is just everything before
    // this page. Deriving the cold case here — rather than wherever the
    // page was set — is what keeps it measured against the page size
    // actually in effect, including a saved view's own.
    page_offset:
      (searchState.keysetId
        ? searchState.pageOffset
        : (searchState.page - 1) * searchState.pageSize) || undefined,
    sort_by:
      activeSorts.length > 0 ? activeSorts.map((s) => s.column) : undefined,
    sort_order:
      activeSorts.length > 0 ? activeSorts.map((s) => s.order) : undefined,
    group_by: searchState.groupBy.length > 0 ? searchState.groupBy : undefined,
    group_row_limit:
      searchState.groupBy.length > 0 ? GROUP_ROW_LIMIT : undefined,
    group_row_limits:
      searchState.groupBy.length > 0 &&
      Object.keys(searchState.groupRowLimits ?? {}).length > 0
        ? searchState.groupRowLimits
        : undefined,
  };
};

// columnWidths is excluded because it's UI-only and shouldn't trigger refetches.
// So are the board's axis and card and the pruned-discriminator note; a field
// left out here must be taken from the current search in
// `useDispatchedSearchState`.
const searchStatesEqual = (
  a: SearchState | null,
  b: SearchState | null
): boolean => {
  if (!a || !b) return a === b;
  return (
    JSON.stringify(a.viewConfig) === JSON.stringify(b.viewConfig) &&
    a.pageSize === b.pageSize &&
    a.keysetId === b.keysetId &&
    a.pageOffset === b.pageOffset &&
    a.page === b.page &&
    a.searchText === b.searchText &&
    JSON.stringify(getValidFilters(a.filters)) ===
      JSON.stringify(getValidFilters(b.filters)) &&
    JSON.stringify(a.visibleColumns) === JSON.stringify(b.visibleColumns) &&
    JSON.stringify(a.sorts) === JSON.stringify(b.sorts) &&
    // Taking over the sort during a search changes whether the column sort (and
    // thus the relevance cutoff) is sent, even when `sorts` is unchanged because
    // the user clicked the column that matched the seeded default (#8908).
    !!a.userSorted === !!b.userSorted &&
    JSON.stringify(a.groupBy) === JSON.stringify(b.groupBy) &&
    JSON.stringify(a.groupRowLimits ?? {}) ===
      JSON.stringify(b.groupRowLimits ?? {})
  );
};

// URL keys owned by the table search state. `syncSearchToUrl` may
// overwrite or delete these when its serialized state changes; any
// other key in the URL (e.g. `?map=` from the map lens, #9528) must
// survive an unrelated table-state sync.
export const TABLE_URL_KEYS = [
  "search",
  "pageSize",
  "page",
  "filters",
  "columns",
  "columnWidths",
  "sortBy",
  "sortOrder",
  "groupBy",
] as const;

// Project current state onto URL params as a *diff* against the active view's
// server baseline. Only fields that differ from server make it into the URL,
// which keeps share-links minimal and ensures the URL stays clean when the
// state matches the saved view exactly.
export const projectStateToUrl = (
  current: SearchState,
  server: SearchState
): URLSearchParams => {
  const params = new URLSearchParams();

  const curSearch = current.searchText || "";
  const srvSearch = server.searchText || "";
  if (curSearch !== srvSearch && curSearch) params.set("search", curSearch);

  if (current.pageSize !== server.pageSize) {
    params.set("pageSize", current.pageSize.toString());
  }

  // The page number, not the cursor: a page is addressable on its own (a
  // cold jump reconstructs it), whereas a bookmarked cursor said only
  // "somewhere after this row" and left the paginator unable to name the
  // page it was on (#10593).
  if (current.page > 1) params.set("page", current.page.toString());

  const curFiltersStr = JSON.stringify(current.filters);
  const srvFiltersStr = JSON.stringify(server.filters);
  if (curFiltersStr !== srvFiltersStr) {
    if (current.filters.length === 0) {
      params.set("filters", "[]");
    } else {
      params.set("filters", curFiltersStr);
    }
  }

  // visibleColumns — when server.visibleColumns is undefined the saved view
  // has no opinion about columns; suppress the diff so the framework default
  // (first-N) doesn't pollute every URL.
  if (
    server.visibleColumns !== undefined &&
    JSON.stringify(current.visibleColumns) !==
      JSON.stringify(server.visibleColumns) &&
    current.visibleColumns &&
    current.visibleColumns.length > 0
  ) {
    params.set("columns", current.visibleColumns.join(","));
  }

  if (
    server.columnWidths !== undefined &&
    JSON.stringify(current.columnWidths) !==
      JSON.stringify(server.columnWidths) &&
    current.columnWidths &&
    Object.keys(current.columnWidths).length > 0
  ) {
    params.set("columnWidths", JSON.stringify(current.columnWidths));
  }

  if (JSON.stringify(current.sorts) !== JSON.stringify(server.sorts)) {
    if (current.sorts.length > 0) {
      params.set("sortBy", current.sorts.map((s) => s.column).join(","));
      params.set("sortOrder", current.sorts.map((s) => s.order).join(","));
    }
  }

  if (JSON.stringify(current.groupBy) !== JSON.stringify(server.groupBy)) {
    if (current.groupBy.length > 0) {
      params.set("groupBy", current.groupBy.join(","));
    }
  }

  return params;
};

export const deserializeFromUrlParams = (
  searchParams: URLSearchParams
): Partial<Omit<SearchState, "viewConfig">> => {
  const state: Partial<Omit<SearchState, "viewConfig">> = {};

  const search = searchParams.get("search");
  if (search) state.searchText = search;

  const pageSize = searchParams.get("pageSize");
  if (pageSize) {
    const parsed = parseInt(pageSize, 10);
    if (!isNaN(parsed) && parsed > 0) state.pageSize = parsed;
  }

  // A bookmarked / shared page reconstructs as a cold jump: the page
  // number is self-contained, so there's no cursor to be out of sync with
  // and no "we're somewhere past page 1 but can't say where" state
  // (#10593).
  //
  // Only the page number is read here. The row offset it implies depends
  // on the page size, which this overlay may not carry — the URL is a
  // *diff* against the saved view, so a view saved at 250/page shares as
  // a bare `?page=4`. Deriving the offset here would silently measure it
  // against the default 50 and land the recipient on the wrong rows.
  // createQueryParams derives it instead, after the merge, where the real
  // page size is known.
  const pageParam = searchParams.get("page");
  if (pageParam) {
    const parsed = parseInt(pageParam, 10);
    if (!isNaN(parsed) && parsed > 1) {
      state.page = parsed;
      // Say the cold-jump part out loud rather than leaning on every
      // caller happening to start from a cursorless baseline.
      state.keysetId = null;
      state.pageOffset = 0;
    }
  }

  const filters = searchParams.get("filters");
  if (filters) {
    try {
      const parsed = JSON.parse(filters);
      if (Array.isArray(parsed)) state.filters = migrateOldFilters(parsed);
    } catch {
      // Ignore invalid JSON
    }
  }

  const sortByParam = searchParams.get("sortBy");
  const sortOrderParam = searchParams.get("sortOrder");
  if (sortByParam) {
    const columns = sortByParam.split(",").filter(Boolean);
    const orders = (sortOrderParam || "").split(",").filter(Boolean);
    state.sorts = columns.map((col, i) => ({
      column: col,
      order: (orders[i] === "asc" || orders[i] === "desc"
        ? orders[i]
        : "asc") as "asc" | "desc",
    }));
  }

  const groupByParam = searchParams.get("groupBy");
  if (groupByParam) {
    state.groupBy = groupByParam.split(",").filter(Boolean);
  }

  const columns = searchParams.get("columns");
  if (columns) {
    state.visibleColumns = columns.split(",").filter(Boolean);
  }

  const columnWidths = searchParams.get("columnWidths");
  if (columnWidths) {
    try {
      const parsed = JSON.parse(columnWidths);
      if (typeof parsed === "object" && parsed !== null) {
        state.columnWidths = parsed;
      }
    } catch {
      // Ignore invalid JSON
    }
  }

  // Backward compat: if old URL has columnOrder, merge it into visibleColumns
  // to preserve the user's preferred column order
  const columnOrder = searchParams.get("columnOrder");
  if (columnOrder && state.visibleColumns) {
    const order = columnOrder.split(",").filter(Boolean);
    const colSet = new Set(state.visibleColumns);
    const orderSet = new Set(order);
    const ordered = order.filter((c) => colSet.has(c));
    const missing = state.visibleColumns.filter((c) => !orderSet.has(c));
    state.visibleColumns = [...ordered, ...missing];
  }

  return state;
};

// Build the server-defaults overlay from a view's saved filters + table_config.
// Used both at store construction (initial baseline) and on Save (re-baseline
// via setServerState).
export function buildViewDefaultsFromConfig(
  viewFilters: FrontendFilter[] | undefined,
  viewTableConfig: TableConfig | null | undefined
): Partial<Omit<SearchState, "viewConfig">> {
  const migratedFilters = viewFilters?.length
    ? migrateOldFilters(viewFilters)
    : undefined;

  // Backward compat: if view has both columns and columnOrder, merge them so
  // columnOrder provides display order and columns provides visibility.
  const cols = viewTableConfig?.columns;
  const order = viewTableConfig?.columnOrder;
  const resolvedColumns =
    order && cols
      ? [
          ...order.filter((c) => cols.includes(c)),
          ...cols.filter((c) => !order.includes(c)),
        ]
      : (order ?? cols);

  // Convert legacy sortBy/sortOrder to sorts array
  const viewSorts: SortEntry[] | undefined =
    viewTableConfig?.sorts ??
    (viewTableConfig?.sortBy
      ? [
          {
            column: viewTableConfig.sortBy,
            order: viewTableConfig.sortOrder ?? "asc",
          },
        ]
      : undefined);
  return {
    ...(migratedFilters?.length ? { filters: migratedFilters } : {}),
    ...Object.fromEntries(
      Object.entries({
        visibleColumns: resolvedColumns,
        columnWidths: viewTableConfig?.columnWidths,
        // A stored searchText is deliberately ignored (#11426): a search
        // term is a way of reaching a row, not part of what a view is, so
        // legacy views that froze one converge to an empty field.
        sorts: viewSorts,
        groupBy: viewTableConfig?.groupBy,
        pageSize: viewTableConfig?.pageSize,
        boardAxis: viewTableConfig?.boardAxis,
        boardCard: viewTableConfig?.boardCard,
      }).filter(([, v]) => v !== undefined && v !== null)
    ),
  };
}

// =============================================================================
// Store factory
// =============================================================================

export interface CreateTableSearchStoreArgs {
  viewConfig: ViewConfig;
  /** Defaults from the view's saved config (filters + table_config). */
  serverDefaults?: Partial<Omit<SearchState, "viewConfig">>;
  /** Overlay parsed from URL search params on first construction. */
  urlOverlay?: Partial<Omit<SearchState, "viewConfig">>;
  /**
   * The host opens its own filters panel (`openFilterRequest`), so the strip's requests stay
   * in this store: a modal's strip must not reach the page behind it.
   */
  ownsFilterPanel?: boolean;
}

/** One request for the filters panel; `seq` tells two requests for the same field apart. */
export interface OpenFilterRequest {
  fieldName: string | null;
  seq: number;
}

export type TableSearchStoreApi = StoreApi<TableSearchStore>;

export function createTableSearchStore({
  viewConfig,
  serverDefaults,
  urlOverlay,
  ownsFilterPanel = false,
}: CreateTableSearchStoreArgs): TableSearchStoreApi {
  const initialServerState = computeServerState(viewConfig, serverDefaults);
  const initialCurrentSearch = migrateForView(
    { ...initialServerState, ...urlOverlay },
    viewConfig
  );

  return createStore<TableSearchStore>((set, get) => ({
    serverState: initialServerState,
    currentSearch: initialCurrentSearch,
    previousSearch: null,
    lastGoodSearch: null,
    intermediarySearch: null,
    lastDispatchedSearch: null,
    data: undefined,
    columnTotals: null,
    error: null,
    hasMore: false,
    pendingDebounce: false,
    pendingGroupTarget: null,
    editedRowIds: new Set<string>(),
    filtersPanelOpen: false,

    setSearchState: (newState, options) => {
      const { currentSearch, data, pendingGroupTarget } = get();

      // Commit-time pending-group application (#10851): an armed target
      // means "the next filter you ADD joins this group", and a filter
      // is added when a clause becomes non-empty — not when the panel
      // inserts an empty placeholder row (merely expanding an attribute
      // section does that, and consuming the target there disarmed the
      // pending group before the user picked any value). The committed-
      // count guard keeps removals (which shift indexes) from reading
      // as commits.
      let effectiveState = newState;
      if (pendingGroupTarget !== null && newState.filters !== undefined) {
        const prev = currentSearch?.filters ?? [];
        const next = newState.filters;
        const committedCount = (fs: FrontendFilter[]): number =>
          fs.filter((f) => !isClauseEmpty(f)).length;
        if (committedCount(next) > committedCount(prev)) {
          const committedIndex = next.findIndex(
            (f, i) =>
              !isClauseEmpty(f) && (i >= prev.length || isClauseEmpty(prev[i]))
          );
          if (committedIndex !== -1) {
            const clause = next[committedIndex];
            const rest = next.filter((_, i) => i !== committedIndex);
            const routed =
              pendingGroupTarget === "new"
                ? insertClauseInNewGroup(rest, clause)
                : insertClauseIntoGroup(rest, clause, pendingGroupTarget);
            effectiveState = { ...newState, filters: routed };
            set({ pendingGroupTarget: null });
          }
        }
      }

      const updatedSearch = { ...currentSearch, ...effectiveState };
      const pendingDebounce = options?.debounce ?? false;

      // Reset the page window when search parameters change (except for
      // the page window itself, or layout-only changes).
      const changedKeys = Object.keys(newState) as (keyof SearchState)[];
      const nonPaginationChange = changedKeys.some(
        (key) =>
          !PAGE_WINDOW_KEYS.has(key) &&
          !PRESENTATION_KEYS.has(key) &&
          // "Show more" in a group asks for more of the page on screen.
          key !== "groupRowLimits"
      );

      // Group allowances are keyed by page-relative rank, so they belong to
      // this page of this question only: anything but presentation or a new
      // allowance (the page window included) drops them.
      if (
        !("groupRowLimits" in newState) &&
        changedKeys.some((key) => !PRESENTATION_KEYS.has(key))
      ) {
        updatedSearch.groupRowLimits = undefined;
      }

      if (nonPaginationChange && !options?.keepPage) {
        // A changed filter / sort / page size re-shapes the result set, so
        // the cursor and the jump it anchors are both meaningless now, and
        // the user is back on page 1. Resetting the page here (rather than
        // reconciling it in the paginator afterwards) is what keeps the
        // page number authoritative instead of inferred (#10593). A caller
        // that opts in with `keepPage` names the page it keeps — that is
        // how a page-size change stays on the row you were reading (#11426).
        updatedSearch.keysetId = null;
        updatedSearch.pageOffset = 0;
        updatedSearch.page = 1;
      }

      // `page` has to be in here, not just the cursor and offset it
      // implies: two different cold pages share `keysetId: null` and
      // `pageOffset: 0`, so without it a cold→cold move (a backward jump,
      // or one from a shared ?page= link) looks like no change at all and
      // leaves the previous page's rows on screen while the next ones load.
      const isPaginationChange =
        ("keysetId" in newState &&
          newState.keysetId !== currentSearch.keysetId) ||
        ("pageOffset" in newState &&
          newState.pageOffset !== currentSearch.pageOffset) ||
        ("page" in newState && newState.page !== currentSearch.page);

      // For layout-only changes, preserve existing data to keep optimistic
      // updates visible.
      const isLayoutChange = changedKeys.some((key) => LAYOUT_KEYS.has(key));

      if (isLayoutChange && !nonPaginationChange && data) {
        set({ currentSearch: updatedSearch, data, pendingDebounce });
      } else if (isPaginationChange) {
        // Clear data on pagination changes so stale records don't flash.
        set({ currentSearch: updatedSearch, data: undefined, pendingDebounce });
      } else {
        set({ currentSearch: updatedSearch, pendingDebounce });
      }
    },

    setIntermediarySearch: (searchState) => {
      // Don't clear data — keep previous data visible with the gray overlay.
      set({
        intermediarySearch: searchState,
        lastDispatchedSearch: searchState,
        error: null,
      });
    },

    setDataAndPreviousSearch: (data, searchState, hasMore, columnTotals) => {
      set({
        data,
        columnTotals: columnTotals ?? null,
        previousSearch: searchState,
        error: null,
        hasMore,
        intermediarySearch: null,
        lastGoodSearch: searchState,
        // A user-initiated result set is a new working set: whatever the
        // user edited under the old one stops earning retention (#11363).
        editedRowIds: new Set<string>(),
      });
    },

    applyBackgroundRefetch: (data, searchState, hasMore, columnTotals) => {
      const { data: previous, editedRowIds } = get();
      // Nothing can stop matching without a filter, so an absent row there
      // is genuinely gone and retaining it would render a ghost.
      const hasActiveFilter = getValidFilters(searchState.filters).length > 0;
      const merged =
        data && previous && hasActiveFilter && editedRowIds.size > 0
          ? mergeRetainedRows(
              previous,
              data,
              editedRowIds,
              searchState.groupBy.length > 0
            )
          : data;
      set({
        data: merged,
        columnTotals: columnTotals ?? null,
        previousSearch: searchState,
        error: null,
        hasMore,
        intermediarySearch: null,
        lastGoodSearch: searchState,
      });
    },

    clearRetainedRows: () => {
      const { data } = get();
      set({
        editedRowIds: new Set<string>(),
        data: data?.filter((row) => !row._retained),
      });
    },

    updateEntityFact: (entityId, factId, newValue) => {
      const { data, editedRowIds } = get();
      if (!data) return;
      set({
        data: data.map((row) =>
          row.id === entityId
            ? { ...row, data: { ...row.data, [factId]: newValue } }
            : row
        ),
        editedRowIds: new Set(editedRowIds).add(entityId),
      });
    },

    updateEntityName: (entityId, newName) => {
      const { data, editedRowIds } = get();
      if (!data) return;
      set({
        data: data.map((row) =>
          row.id === entityId ? { ...row, name: newName } : row
        ),
        editedRowIds: new Set(editedRowIds).add(entityId),
      });
    },

    setPendingGroupTarget: (target) => set({ pendingGroupTarget: target }),

    setFiltersPanelOpen: (open) => set({ filtersPanelOpen: open }),

    openFilterRequest: null,
    requestOpenFilter: (fieldName) => {
      if (ownsFilterPanel) {
        set((state) => ({
          openFilterRequest: {
            fieldName,
            seq: (state.openFilterRequest?.seq ?? 0) + 1,
          },
        }));
        return;
      }
      window.dispatchEvent(
        new CustomEvent(
          "rms-open-filter",
          fieldName === null ? {} : { detail: { fieldName } }
        )
      );
    },

    setError: (error) =>
      set({
        error,
        data: undefined,
        columnTotals: null,
        hasMore: false,
        intermediarySearch: null,
        // Clearing previousSearch makes the next setSearchState always stale,
        // re-triggering the search after an error.
        previousSearch: null,
      }),

    setServerState: (viewConfig, serverDefaults) => {
      const next = computeServerState(viewConfig, serverDefaults);
      const { serverState } = get();
      if (JSON.stringify(serverState) === JSON.stringify(next)) return;
      set({ serverState: next });
    },

    clearData: () => {
      // Drop visible rows + reset pagination so the next dispatch refetches
      // fresh. Keep serverState (the view baseline doesn't change just
      // because we cleared the table).
      set((state) => ({
        data: undefined,
        columnTotals: null,
        error: null,
        hasMore: false,
        intermediarySearch: null,
        previousSearch: null,
        lastGoodSearch: null,
        currentSearch: {
          ...state.currentSearch,
          keysetId: null,
          pageOffset: 0,
          page: 1,
        },
      }));
    },

    forceRefresh: () => {
      // Setting previousSearch to null makes isStale true, which re-triggers
      // the debounced search effect.
      set({ previousSearch: null });
    },

    goToPage: (target, cursor) => {
      const { currentSearch } = get();
      const page = Math.max(1, target);
      if (page === currentSearch.page) return;

      const size = currentSearch.pageSize;
      // Forward from a known cursor: skip only the pages between here and
      // there, so the offset is the distance jumped rather than the depth
      // reached. Otherwise (backward, or no cursor) restart from the top —
      // a cursor only points forward.
      const canRideCursor = page > currentSearch.page && cursor !== null;
      const window = canRideCursor
        ? {
            keysetId: cursor,
            pageOffset: (page - currentSearch.page - 1) * size,
          }
        : // Cold jump: no cursor, so the skip is implied by the page number
          // alone and createQueryParams derives it. Storing one here would
          // be a second copy of the same fact, free to go stale.
          { keysetId: null, pageOffset: 0 };

      get().setSearchState({ ...window, page });
    },

    resetToFirstPage: () =>
      get().setSearchState({ keysetId: null, pageOffset: 0, page: 1 }),
  }));
}

// =============================================================================
// Context + Provider + hooks
// =============================================================================

const TableSearchContext = createContext<TableSearchStoreApi | null>(null);

export function TableSearchProvider({
  store,
  children,
}: PropsWithChildren<{ store: TableSearchStoreApi }>) {
  return (
    <TableSearchContext.Provider value={store}>
      {children}
    </TableSearchContext.Provider>
  );
}

/**
 * "Show more" for one group of the page on screen: raises that group's row
 * allowance, which rides on the page's own request, so the page refetches
 * with the group's next rows and any later refetch of it returns them too.
 * The page itself stays where it is.
 */
export function useShowMoreInGroup(): (rank: number) => void {
  const store = useTableSearchStoreApi();
  return useCallback(
    (rank: number) => {
      const { currentSearch, setSearchState } = store.getState();
      setSearchState({
        groupRowLimits: withMoreRowsFor(currentSearch.groupRowLimits, rank),
      });
    },
    [store]
  );
}

/** Get the raw store API for the active view from context. Throws if no provider. */
export function useTableSearchStoreApi(): TableSearchStoreApi {
  const store = useContext(TableSearchContext);
  if (!store) {
    throw new Error("useTableSearchStore: TableSearchProvider missing in tree");
  }
  return store;
}

// Selector hook (overloaded — no-arg returns full state). Mirrors the
// shape of the previous module-level zustand hook so consumer code stays
// unchanged at call sites.
export function useTableSearchStore(): TableSearchStore;
export function useTableSearchStore<T>(
  selector: (state: TableSearchStore) => T
): T;
export function useTableSearchStore<T>(
  selector?: (state: TableSearchStore) => T
): T | TableSearchStore {
  const store = useTableSearchStoreApi();
  // Cast: the overload signature handles both branches; useStore needs a selector.
  return useStore(
    store,
    (selector ?? ((s) => s)) as (state: TableSearchStore) => T
  );
}

export const useServerState = (): SearchState =>
  useTableSearchStore((s) => s.serverState);

/** The holdings search's column totals for the rows on screen (#14991). */
export const useSearchColumnTotals = (): Record<string, number> | null =>
  useTableSearchStore((s) => s.columnTotals);

export const useSearchData = () => {
  const {
    currentSearch,
    previousSearch,
    lastGoodSearch,
    data,
    error,
    hasMore,
    setSearchState,
    pendingGroupTarget,
    setPendingGroupTarget,
    filtersPanelOpen,
    setFiltersPanelOpen,
  } = useTableSearchStore();

  const isStale = !searchStatesEqual(currentSearch, previousSearch);

  return {
    data,
    error,
    hasMore,
    isStale,
    searchState: currentSearch,
    previousSearchState: previousSearch,
    lastGoodSearch,
    setSearchState,
    pendingGroupTarget,
    setPendingGroupTarget,
    filtersPanelOpen,
    setFiltersPanelOpen,
  };
};

// The search the table has asked the server for. It is the current search,
// except while a typed change waits out TYPED_SEARCH_DEBOUNCE_MS: then what
// shapes a request (term, filters, sort, page, columns, grouping) comes from
// the last search that was dispatched. A request that rides beside the search
// (the count, a board lane) keys on this, so it is sent once per dispatched
// search instead of once per keystroke (#12511).
//
// Layout is never dispatched, so it is never held back: no dispatch carries
// it, and a board that read it from the dispatched search kept its old lanes
// under a control naming the new ones. The layout fields are the ones
// `searchStatesEqual` leaves out.
export const useDispatchedSearchState = (): SearchState => {
  const currentSearch = useTableSearchStore((s) => s.currentSearch);
  const dispatched = useTableSearchStore((s) => s.lastDispatchedSearch);
  const pendingDebounce = useTableSearchStore((s) => s.pendingDebounce);
  return useMemo(() => {
    // A click-driven change is dispatched on the next effect: nothing waits.
    if (!pendingDebounce || !dispatched) return currentSearch;
    // `pendingDebounce` stays true after a typed change is dispatched, until
    // the next `setSearchState`, so the flag alone does not say a change is
    // waiting: this comparison does. Nothing a request carries differs, so
    // hand back the current search itself and keep the reference stable.
    if (searchStatesEqual(currentSearch, dispatched)) return currentSearch;
    return {
      ...dispatched,
      columnWidths: currentSearch.columnWidths,
      boardAxis: currentSearch.boardAxis,
      boardCard: currentSearch.boardCard,
      prunedDiscriminator: currentSearch.prunedDiscriminator,
    };
  }, [currentSearch, dispatched, pendingDebounce]);
};

export const useSearchVisibleColumns = (
  schema?: RmsSchema
): string[] | undefined => {
  const { searchState } = useSearchData();

  if (searchState.visibleColumns) {
    return expandReviewTrio(searchState.visibleColumns, schema);
  }
  if (schema) {
    return getDefaultVisibleColumns(searchState.viewConfig, schema);
  }
  return undefined;
};

export const useSearchColumnWidths = (): Record<string, number> | undefined => {
  const { searchState } = useSearchData();
  return searchState.columnWidths;
};

// =============================================================================
// TableSearchWorker
// =============================================================================

// Mounts inside a TableSearchProvider. Pure subscriber/dispatcher: subscribes
// to its store, fires tanstack-query when state is stale, writes URL on
// state changes. Initialization is done by the parent (factory call); the
// worker never mutates the store from render body.
interface TableSearchWorkerProps {
  viewConfig: ViewConfig;
  viewFilters?: FrontendFilter[];
  viewTableConfig?: TableConfig | null;
  /**
   * The view at rest, for a host with no saved view (the claims queue: each
   * pile is its own baseline). Given, it is the baseline in place of the
   * saved view's config, so the host's own defaults never read as a change
   * and are never written into the URL. Stable identity, like the others.
   */
  baseline?: Partial<Omit<SearchState, "viewConfig">>;
}

export const TableSearchWorker: React.FC<TableSearchWorkerProps> = ({
  viewConfig,
  viewFilters,
  viewTableConfig,
  baseline,
}) => {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const queryClient = useQueryClient();
  // The source the grid's rows come from (#13370, round 36b): its cache entries are the ones the
  // freshness check reads.
  const source = useRecordDataSource();
  const store = useTableSearchStoreApi();
  // A page drawn for looking (the Appearance editor's preview, #13370 round 42) never writes the
  // address: the editor's own parameters live there (`page` names its record type, which the
  // grid read as its page number and dropped, and the editor's leave guard took the write for
  // leaving).
  const still = useContext(StillPageContext);
  const stillRef = useRef(still);
  stillRef.current = still;

  const currentSearch = useTableSearchStore((s) => s.currentSearch);
  const intermediarySearch = useTableSearchStore((s) => s.intermediarySearch);
  const pendingDebounce = useTableSearchStore((s) => s.pendingDebounce);
  const {
    setIntermediarySearch,
    setDataAndPreviousSearch,
    applyBackgroundRefetch,
    setError,
  } = useTableSearchStore();
  const { isStale, previousSearchState } = useSearchData();
  const debouncedSetIntermediarySearch = useDebouncedCallback(
    (search: SearchState) => setIntermediarySearch(search),
    TYPED_SEARCH_DEBOUNCE_MS
  );

  // Function to sync search state to URL. URL is the diff between
  // currentSearch and serverState — only differing fields are projected.
  // Both are read from the store when the write happens, never from the
  // caller's render: an effect that lands late (a response for the search
  // before the reader's last change, on a busy page) would otherwise project
  // its older search against the newer baseline and write a diff nobody
  // made — on the claims page, the last pile's filters over the new pile.
  const syncSearchToUrl = useCallback(() => {
    if (stillRef.current) return;
    const { currentSearch: searchState, serverState } = store.getState();
    const newParams = projectStateToUrl(searchState, serverState);

    // Preserve query params we don't own (e.g. `?map=` from the map lens).
    const currentUrlParams = new URLSearchParams(window.location.search);
    for (const [k, v] of currentUrlParams) {
      if (!(TABLE_URL_KEYS as readonly string[]).includes(k)) {
        newParams.set(k, v);
      }
    }

    const newParamsStr = newParams.toString();
    const currentParamsStr = window.location.search.replace(/^\?/, "");
    if (newParamsStr !== currentParamsStr) {
      const path = window.location.pathname;
      navigateRef.current(newParamsStr ? `${path}?${newParamsStr}` : path, {
        replace: true,
      });
    }
  }, [store]);

  const isCachedAndFresh = useCallback(
    (searchState: SearchState): boolean => {
      const queryParams = createQueryParams(searchState);
      const queryKey = searchQueryKeyFor(queryParams, source);
      const queryState = queryClient.getQueryState(queryKey);
      if (!queryState?.data) return false;
      const now = Date.now();
      return queryState.dataUpdatedAt + SEARCH_STALE_TIME > now;
    },
    [queryClient, source]
  );

  // Resync `serverState` whenever the view's saved config changes (e.g. Save
  // optimistically updates table_config via props). Without this, the URL
  // projection keeps using the pre-Save baseline and won't clear matching
  // params from URL.
  useEffect(() => {
    const setServerState = store.getState().setServerState;
    setServerState(
      viewConfig,
      baseline ?? buildViewDefaultsFromConfig(viewFilters, viewTableConfig)
    );
    // syncSearchToUrl is keyed on `currentSearch` so it won't fire when only
    // serverState changes. Manually re-project so the URL params clear when
    // current state now matches the new server baseline.
    syncSearchToUrl();
  }, [
    viewConfig,
    viewFilters,
    viewTableConfig,
    baseline,
    store,
    syncSearchToUrl,
  ]);

  // Create query params for Tanstack Query — use intermediarySearch for
  // active searches, fall back to previousSearch to keep the query active for
  // background refetches.
  const activeSearchState = intermediarySearch ?? previousSearchState;
  const queryParams: SearchParams | null = useMemo(() => {
    if (!activeSearchState) return null;
    return createQueryParams(activeSearchState);
  }, [activeSearchState]);

  const queryResult = useSearchEntities(queryParams!, !!queryParams);

  // Track the last consumed dataUpdatedAt so background-refetches with no
  // observer change don't double-consume.
  const lastConsumedAtRef = useRef(0);

  // Handle successful query results (user-initiated searches).
  useEffect(() => {
    if (queryResult.data && intermediarySearch && !queryResult.error) {
      const { rows: rawData, columnTotals } = queryResult.data;
      const { data, hasMore } = sliceForPage(rawData, intermediarySearch);
      lastConsumedAtRef.current = queryResult.dataUpdatedAt;
      setDataAndPreviousSearch(data, intermediarySearch, hasMore, columnTotals);
      syncSearchToUrl();
    }
  }, [
    queryResult.data,
    queryResult.dataUpdatedAt,
    intermediarySearch,
    queryResult.error,
    setDataAndPreviousSearch,
    syncSearchToUrl,
  ]);

  // Handle background refetch results (e.g. after mutations invalidate
  // queries). The primary effect only fires when intermediarySearch is set
  // (user-initiated). This effect picks up refetch data via previousSearch.
  useEffect(() => {
    if (intermediarySearch) return;
    if (!queryResult.data || queryResult.error) return;
    if (!previousSearchState) return;
    if (queryResult.dataUpdatedAt <= lastConsumedAtRef.current) return;

    lastConsumedAtRef.current = queryResult.dataUpdatedAt;
    const { rows: rawData, columnTotals } = queryResult.data;
    const { data, hasMore } = sliceForPage(rawData, previousSearchState);
    // Merge rather than replace: a write the user just made is what
    // triggered this refetch, so the row they are standing on must not be
    // evicted just because their own edit stopped it matching (#11363).
    applyBackgroundRefetch(data, previousSearchState, hasMore, columnTotals);
  }, [
    queryResult.data,
    queryResult.dataUpdatedAt,
    queryResult.error,
    intermediarySearch,
    previousSearchState,
    applyBackgroundRefetch,
  ]);

  // Handle query errors.
  useEffect(() => {
    if (queryResult.error) {
      let errorMessage = "Search failed";
      if (queryResult.error instanceof Error) {
        errorMessage = queryResult.error.message;
        if (errorMessage.includes("fetch")) {
          errorMessage = "Network error - please check your connection";
        } else if (errorMessage.includes("404")) {
          errorMessage = "Search endpoint not found";
        } else if (
          errorMessage.includes("401") ||
          errorMessage.includes("403")
        ) {
          errorMessage = "Access denied - please check your permissions";
        } else if (errorMessage.includes("429")) {
          errorMessage =
            "Too many requests - please wait a moment and try again";
        } else if (
          errorMessage.includes("502") ||
          errorMessage.includes("503")
        ) {
          errorMessage = "Service temporarily unavailable";
        }
      }
      setError(errorMessage);
    }
  }, [queryResult.error, setError]);

  // Search-dispatch effect. Click-driven changes fire immediately; opt-in
  // keystroke streams (text search, typed filter values) wait out
  // TYPED_SEARCH_DEBOUNCE_MS.
  useEffect(() => {
    if (!isStale) return;

    if (!pendingDebounce || isCachedAndFresh(currentSearch)) {
      setIntermediarySearch(currentSearch);
      return;
    }

    debouncedSetIntermediarySearch(currentSearch);
    // A newer search, or the search going fresh, drops the pending one.
    return debouncedSetIntermediarySearch.cancel;
  }, [
    currentSearch,
    isStale,
    pendingDebounce,
    setIntermediarySearch,
    isCachedAndFresh,
    debouncedSetIntermediarySearch,
  ]);

  // Sync search state to URL whenever currentSearch changes (catches
  // UI-only changes like columnWidths that don't trigger refetches).
  useEffect(() => {
    syncSearchToUrl();
  }, [currentSearch, syncSearchToUrl]);

  return null;
};

// =============================================================================
// URL-overlay helper for RMSPage store construction
// =============================================================================

/**
 * Parse URL search params into a partial SearchState overlay, for use when
 * constructing a TableSearchStore for the view whose slug matches the
 * current URL. Stores for other views start with no overlay (URL params
 * only belong to the active view in the URL slug).
 */
export function readUrlOverlay(
  searchParams: URLSearchParams
): Partial<Omit<SearchState, "viewConfig">> {
  if (!searchParams.toString()) return {};
  return deserializeFromUrlParams(searchParams);
}

// =============================================================================
// Live store registry + cross-view refresh API
// =============================================================================

// Module-level registry of live per-view stores, keyed by view id. Lives at
// module scope (not inside any component) so the stores survive any
// navigation within the SPA — including leaving and returning to /rms.
// RMSPage reads/writes via `registerRmsStore` / `unregisterRmsStore` /
// `getRmsStore`; nothing else touches this directly.
//
// Lifetime: a store enters on first storeFor(view) call, exits when the
// view is hidden or deleted. The map is a plain ref — mutations don't
// trigger React renders.
const rmsActiveStores = new Map<number, TableSearchStoreApi>();

export function getRmsStore(viewId: number): TableSearchStoreApi | undefined {
  return rmsActiveStores.get(viewId);
}

export function registerRmsStore(
  viewId: number,
  store: TableSearchStoreApi
): void {
  rmsActiveStores.set(viewId, store);
}

export function unregisterRmsStore(viewId: number): void {
  rmsActiveStores.delete(viewId);
}

/** Start a fresh page lifetime. Ordinary in-app navigation keeps these stores. */
export function resetRmsStores(): void {
  rmsActiveStores.clear();
}

/**
 * Imperative "blow away the RMS search caches" handle. Clears every active
 * per-view store's data + drops cached search results from tanstack-query so
 * the next observer attach fetches fresh. Safe to call from anywhere —
 * with no live RMSPage the store iteration is a no-op and only the
 * tanstack-query invalidation runs.
 */
export function useRefreshRmsSearch(): () => void {
  const queryClient = useQueryClient();
  return useCallback(() => {
    for (const store of rmsActiveStores.values()) {
      store.getState().clearData();
    }
    queryClient.removeQueries({
      predicate: (q) => q.queryKey[0] === "rms" && q.queryKey[1] === "search",
    });
  }, [queryClient]);
}
